import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, Container, Row, Col, Badge, Button, Card, Spinner } from 'react-bootstrap';
import { fetchTeacherById, toggleFollowTeacher } from '../../../store/slices/teachersSlice';
import { StarRating } from '../../marketplace/components/StarRating';
import { CourseCard } from '../../marketplace/components/CourseCard';
import './teachers-details.scss';

// Fallback assets
import teacherPlaceholder from '../../../assets/teacher_profile.png';
import thumbPlaceholder from '../../../assets/video_thumbnail.png';

export const TeachersDetails = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { currentTeacher: data, loading, error } = useSelector((state) => state.teachers);
    const [showFullBio, setShowFullBio] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(fetchTeacherById(id));
        }
    }, [dispatch, id]);

    const handleFollowToggle = () => {
        if (data) {
            dispatch(toggleFollowTeacher({ id: data.teacher._id, isFollowing: data.is_follow }));
        }
    };

    const handleShare = async (platform) => {
        const shareUrl = data?.shareUrlWeb || window.location.href;
        const text = `Check out ${name}'s profile on Yoospeak!`;

        if (platform === 'copy') {
            navigator.clipboard.writeText(shareUrl);
            alert('Link copied to clipboard!');
            return;
        }

        if (navigator.share && platform === 'native') {
            try {
                await navigator.share({ title: 'Yoospeak Instructor', text, url: shareUrl });
            } catch (err) { console.log('Share failed:', err); }
            return;
        }

        const urls = {
            fb: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
            tw: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
            wa: `https://api.whatsapp.com/send?text=${encodeURIComponent(text + ' ' + shareUrl)}`
        };

        if (urls[platform]) window.open(urls[platform], '_blank');
    };

    const toggleBio = () => setShowFullBio(!showFullBio);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <Container className="py-5 text-center">
                <div className="alert alert-danger">{error || 'Teacher not found'}</div>
                <Link to="/marketplace" className="btn btn-primary">Back to Marketplace</Link>
            </Container>
        );
    }

    const { 
        teacher, 
        languages, 
        teacher_rating, 
        follower_count, 
        following_count, 
        country, 
        my_posts = [],
        upcoming_classes = [],
        courses = [],
        teacher_reviews,
        shareUrlWeb,
        isSubscriptionEnabled
    } = data;

    const profilePic = teacher?.profile_picture || teacherPlaceholder;
    const coverPic = teacher?.profile_cover_image || null;
    const name = teacher?.full_name || data.name || 'Instructor';
    const rating = parseFloat(teacher_reviews?.average_rating || teacher_rating || 0);
    const reviewCount = teacher_reviews?.total_reviews || 0;

    // Derive specialties from keypoints in classes or hardcoded if missing
    const specialties = (upcoming_classes[0]?.keypoints || ['Conversational English', 'Business English', 'English for Traveling']).filter(k => k !== 'trending' && k !== 'recommended');

    return (
        <section className="teachers-details-page py-4">
            <Container fluid="xl">
                {coverPic && (
                    <div className="teacher-cover-image mb-4 rounded-3 overflow-hidden shadow-sm" style={{ height: '300px' }}>
                        <img src={coverPic} alt="Cover" className="w-100 h-100 object-fit-cover" />
                    </div>
                )}

                <div className="layout-wrapper">
                    {/* Main Content Column */}
                    <div className="teacher-main-content">
                        {/* Teacher Header */}
                        <div className="teacher-header text-gray-900 d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                            <div className="teacher-profile d-flex align-items-center gap-4">
                                <div className="profile-img-container position-relative">
                                    <img 
                                        src={profilePic} 
                                        alt={name} 
                                        className="border border-4 border-white rounded-circle shadow-lg" 
                                        style={{ height: '100px', width: '100px', objectFit: 'cover' }} 
                                    />
                                    {isSubscriptionEnabled && (
                                        <Badge bg="warning" className="position-absolute bottom-0 end-0 rounded-circle p-2 shadow-sm" title="Subscription Enabled">
                                            <i className="bi bi-star-fill text-white"></i>
                                        </Badge>
                                    )}
                                </div>
                                <span className="teacher-info text-capitalize">
                                    <h1 className="teacher-name fw-bold fs-2 text-gray-900 mb-1">{name}</h1>
                                    <div className="teacher-tagline text-gray-600 fs-5">
                                        {teacher?.user_type === 'teacher' ? 'Professional Language Mentor' : 'Creative Content Instructor'}
                                    </div>
                                </span>
                            </div>
                            <div className="country-info d-flex align-items-center gap-2 bg-white px-4 py-2 rounded-pill shadow-sm border">
                                <span className="country-flag fs-4">{teacher?.country_code === 'IN' ? '🇮🇳' : '🌐'}</span>
                                <span className="country-name fw-bold text-gray-700">{country || 'International'}</span>
                            </div>
                        </div>

                        {/* Summary Text / Experience */}
                        <div className="experience-badges text-base text-gray-700 mb-4 d-flex flex-wrap align-items-center gap-2">
                            <Badge bg="primary" className="fw-semibold px-4 py-2 rounded-pill">
                                {teacher?.experience > 0 ? `${teacher.experience}+ Years Experience` : 'Expert Mentor'}
                            </Badge>
                            {teacher?.is_blue_verified && (
                                <Badge bg="info" className="d-flex align-items-center gap-2 px-4 py-2 rounded-pill shadow-sm">
                                    <i className="bi bi-patch-check-fill"></i> Blue Verified
                                </Badge>
                            )}
                            {data.slot_counts?.total_slots > 0 && (
                                <Badge bg="success" className="bg-opacity-10 text-success border border-success border-opacity-25 px-4 py-2 rounded-pill">
                                    {data.slot_counts.total_slots} Slots Available
                                </Badge>
                            )}
                        </div>

                        {/* Info Bar / Stats */}
                        <div className="stats-info-bar bg-white border border-light-subtle d-flex flex-wrap align-items-center justify-content-between gap-3 rounded-4 mb-5 px-4 py-4 shadow-sm">
                            <div className="dynamic-info d-flex align-items-center gap-4">
                                <div className="stat-item text-center">
                                    <div className="fw-black fs-3 text-primary">{follower_count}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Followers</div>
                                </div>
                                <div className="stat-item text-center ps-4 border-start">
                                    <div className="fw-black fs-3 text-primary">{following_count}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Following</div>
                                </div>
                                <div className="stat-item text-center ps-4 border-start">
                                    <div className="fw-black fs-3 text-primary">{my_posts.length}</div>
                                    <div className="text-xs text-gray-500 fw-bold text-uppercase tracking-wider">Posts</div>
                                </div>
                            </div>
                            <div className="rating-info d-flex flex-column align-items-end gap-1">
                                <div className="stars d-flex gap-1 fs-5">
                                    <StarRating rating={rating} />
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="rating-val text-gray-900 fw-black fs-4">{rating.toFixed(1)}</span>
                                    <span className="rating-count text-gray-500 text-sm fw-medium">({reviewCount} Verified Reviews)</span>
                                </div>
                            </div>
                        </div>

                        {/* Specialties Section (Accordion) */}
                        <div className="specialties-section mb-5">
                             <div className="main-heading mb-4 fs-4 fw-bold">My Specialties</div>
                             <Accordion className="custom-accordion border-0 shadow-sm rounded-4 overflow-hidden">
                                {specialties.map((spec, idx) => (
                                    <Accordion.Item eventKey={idx.toString()} key={idx} className="border-bottom border-light">
                                        <Accordion.Header className="fw-semibold">{spec}</Accordion.Header>
                                        <Accordion.Body className="text-gray-600">
                                            Tailored sessions focused on {spec.toLowerCase()}, helping you achieve fluency and confidence through practical exercises and real-world scenarios.
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                             </Accordion>
                        </div>

                        {/* About Me Section */}
                        <div className="about-section mb-5">
                            <div className="main-heading mb-3 fs-4 fw-bold">About me</div>
                            <div className="description-text text-gray-700 bg-white p-4 rounded-4 border shadow-sm">
                                <div className={`bio-content ${!showFullBio && teacher?.description?.length > 400 ? 'text-truncate-4' : ''}`} style={{whiteSpace: 'pre-line'}}>
                                    {teacher?.description || 'No detailed bio provided yet. Explore my courses and classes to learn more about my teaching style.'}
                                </div>
                                {teacher?.description?.length > 400 && (
                                    <Button variant="link" className="p-0 mt-3 text-decoration-none fw-bold text-primary" onClick={toggleBio}>
                                        {showFullBio ? 'Read less' : 'Read more'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* My Languages Section */}
                        <div className="language-section mb-5">
                            <div className="main-heading mb-3 fs-4 fw-bold">My Languages</div>
                            <div className="d-flex flex-wrap gap-3">
                                {languages ? languages.split(',').map((lang, idx) => (
                                    <div key={idx} className="lang-item bg-white border border-light-subtle d-flex align-items-center gap-3 px-4 py-2 rounded-pill shadow-sm">
                                        <div className="lang-icon bg-primary bg-opacity-10 text-primary rounded-circle flex-center" style={{width:'32px', height:'32px'}}>
                                            <i className="bi bi-translate"></i>
                                        </div>
                                        <span className="lang-name fw-bold text-gray-800">{lang.trim()}</span>
                                        <Badge bg="success" className="bg-opacity-10 text-success fw-semibold px-3 py-1">Proficient</Badge>
                                    </div>
                                )) : <div className="text-muted">No languages specified.</div>}
                            </div>
                        </div>

                        {/* Upcoming Classes Section */}
                        {upcoming_classes.length > 0 && (
                            <div className="upcoming-classes mb-5">
                                <div className="main-heading mb-4 fs-4 fw-bold">Live Upcoming Classes</div>
                                <Row className="g-4">
                                    {upcoming_classes.map((cls) => (
                                        <Col md={6} key={cls._id}>
                                            <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden card-hover">
                                                <div className="bg-primary bg-opacity-10 p-4 pb-2">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <Badge bg={cls.type === 'one_to_one' ? 'info' : 'primary'} className="rounded-pill px-3 py-2">
                                                            {cls.type === 'one_to_one' ? '1-on-1' : 'Group Session'}
                                                        </Badge>
                                                        <span className="fs-3 fw-black text-primary">${cls.cost || 0}</span>
                                                    </div>
                                                    <h3 className="fs-5 fw-bold text-gray-900 mb-0">{cls.title}</h3>
                                                </div>
                                                <Card.Body className="p-4 pt-0">
                                                    <div className="text-sm text-gray-600 my-3">
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <i className="bi bi-calendar3 text-primary"></i>
                                                            <span className="fw-medium">{cls.start_date}</span>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="bi bi-clock text-primary"></i>
                                                            <span className="fw-medium">{cls.start_time} - {cls.end_time}</span>
                                                        </div>
                                                    </div>
                                                    <Button variant="primary" className="w-100 rounded-3 py-2 fw-bold">Book This Session</Button>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}

                        {/* My Posts (Video Feed) */}
                        {my_posts.length > 0 && (
                            <div className="my-posts-section mb-5">
                                <div className="main-heading mb-4 fs-4 fw-bold">Latest Posts</div>
                                <Row className="g-4 row-cols-1 row-cols-sm-2 row-cols-md-3">
                                    {my_posts.map((post) => (
                                        <Col key={post._id}>
                                            <div className="video-card rounded-4 overflow-hidden shadow-sm border h-100 bg-white shadow-hover transition-all">
                                                <div className="ratio ratio-16x9 position-relative cursor-pointer">
                                                    <img src={post.thumbnailUrl || thumbPlaceholder} alt={post.title} className="object-fit-cover" />
                                                    <div className="play-btn-overlay position-absolute top-50 start-50 translate-middle bg-white bg-opacity-90 rounded-circle flex-center shadow-lg" style={{width: '45px', height: '45px'}}>
                                                        <i className="bi bi-play-fill text-primary fs-3"></i>
                                                    </div>
                                                </div>
                                                <div className="p-3">
                                                    <div className="fw-bold text-gray-900 text-truncate mb-2">{post.title}</div>
                                                    <div className="text-xs text-gray-500 d-flex justify-content-between align-items-center">
                                                        <span className="d-flex align-items-center gap-1">
                                                            <i className="bi bi-eye"></i> {post.views} views
                                                        </span>
                                                        <span className="bg-light px-2 py-1 rounded">{new Date(post.createdAt).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}

                        {/* Courses Section */}
                        {courses.length > 0 && (
                            <div className="courses-section">
                                <div className="main-heading mb-4 fs-4 fw-bold">Popular Courses</div>
                                <Row className="g-4 row-cols-1 row-cols-sm-2">
                                    {courses.map((course) => (
                                        <Col key={course._id}>
                                            <CourseCard course={course} />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}
                    </div>

                    {/* Sidebar Column */}
                    <div className="sidebar-sticky-col">
                        <Card className="sidebar-wrapper border-0 rounded-4 shadow-xl overflow-hidden">
                            <div className="bg-primary text-white p-5 text-center position-relative overflow-hidden">
                                <div className="decoration-circle position-absolute" style={{top:'-20px', right:'-20px', width:'100px', height:'100px', background:'rgba(255,255,255,0.1)', borderRadius:'50%'}}></div>
                                <div className="text-sm text-uppercase fw-black opacity-75 mb-1 tracking-widest">Base Rate</div>
                                <div className="d-flex align-items-center justify-content-center gap-3">
                                    <span className="display-4 fw-black">$20.00</span>
                                    <span className="text-decoration-line-through fs-5 opacity-60">$40.00</span>
                                </div>
                                <Badge bg="light" text="dark" className="mt-3 rounded-pill px-4 py-2 fw-bold">50% INTRO DISCOUNT</Badge>
                            </div>
                            
                            <Card.Body className="p-4 p-xl-5">
                                <div className="cta-buttons d-flex flex-column gap-3 mb-4">
                                    <Button className="btn-custom py-3 fs-5 fw-black shadow-lg rounded-3">
                                        <i className="bi bi-lightning-charge-fill me-2"></i> Book Trial Lesson
                                    </Button>
                                    <div className="d-flex gap-3">
                                        <Button variant="outline-primary" className="flex-grow-1 py-3 fw-bold rounded-3">
                                            <i className="bi bi-chat-dots-fill me-2"></i> Message
                                        </Button>
                                        <Button 
                                            variant={data.is_follow ? "primary" : "outline-primary"} 
                                            className="px-4 py-3 rounded-3 shadow-hover transition-all"
                                            onClick={handleFollowToggle}
                                        >
                                            <i className={`bi ${data.is_follow ? "bi-person-check-fill shadow-glow" : "bi-person-plus-fill"}`}></i>
                                        </Button>
                                    </div>
                                </div>

                                <div className="includes-section bg-light bg-opacity-50 p-4 rounded-4">
                                    <div className="fw-bold text-gray-900 mb-4 text-uppercase tracking-wider fs-xs">Why learn with {name.split(' ')[0]}?</div>
                                    <ul className="list-unstyled d-flex flex-column gap-4 text-sm text-gray-700">
                                        <li className="d-flex align-items-start gap-3">
                                            <div className="bg-success bg-opacity-10 rounded-circle flex-center" style={{minWidth:'30px', height:'30px'}}>
                                                <i className="bi bi-shield-check text-success"></i>
                                            </div>
                                            <span>Verified Identity & Certified Teaching Professional</span>
                                        </li>
                                        <li className="d-flex align-items-start gap-3">
                                            <div className="bg-primary bg-opacity-10 rounded-circle flex-center" style={{minWidth:'30px', height:'30px'}}>
                                                <i className="bi bi-camera-video text-primary"></i>
                                            </div>
                                            <span>Full Interactive Experience & Free Introductory Session</span>
                                        </li>
                                        <li className="d-flex align-items-start gap-3">
                                            <div className="bg-warning bg-opacity-10 rounded-circle flex-center" style={{minWidth:'30px', height:'30px'}}>
                                                <i className="bi bi-clock-history text-warning"></i>
                                            </div>
                                            <span>Instant Scheduling & Life-time access to course materials</span>
                                        </li>
                                    </ul>
                                </div>

                                <hr className="my-5 opacity-10" />

                                <div className="share-section text-center">
                                    <div className="text-xs fw-black text-gray-400 mb-4 text-uppercase tracking-widest">Share this profile</div>
                                    <div className="share-actions d-flex justify-content-center gap-3">
                                        {[
                                            { icon: 'bi-facebook', key: 'fb' },
                                            { icon: 'bi-twitter-x', key: 'tw' },
                                            { icon: 'bi-whatsapp', key: 'wa' },
                                            { icon: 'bi-link-45deg', key: 'copy' }
                                        ].map((soc) => (
                                            <div 
                                                key={soc.key} 
                                                className="share-icon-circle pointer transition-all shadow-sm"
                                                onClick={() => handleShare(soc.key)}
                                            >
                                                <i className={`bi ${soc.icon} fs-5`}></i>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-5 pt-4 border-top text-center">
                                    <a 
                                        href={data.shareUrlApp || '#'} 
                                        className="btn btn-link text-decoration-none text-primary fw-black small text-uppercase tracking-wider"
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                    >
                                        <i className="bi bi-phone-fill me-2 fs-5"></i> Open In App
                                    </a>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </Container>
        </section>
    );
};